import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { secondsToTime, toDisplayTimeString } from '../../helpers/moment-ex';

export default function TripHistoryChart(props) {
    const { data, timezoneIdentifier } = props;
    const [chartInfo, setChartInfo] = useState({});

    useEffect(() => {
        let result = {};
        const newChartseries = [];
        const chatXAxis = ['12:01 AM', '01:00 AM', '02:00 AM', '03:00 AM', '04:00 AM', '05:00 AM', '06:00 AM', '07:00 AM', '08:00 AM',
            '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM', '06:00 PM',
            '07:00 PM', '08:00 PM', '09:00 PM', '10:00 PM', '11:00 PM', "11:59 PM"];

        const regions = data.map((e) => e.regionName);
        const chartYAxis = [...new Set(regions)];
        const getEventDate = data && data.length !== 0 ? data.map((item) => moment.utc(item.exit).tz(timezoneIdentifier).format('YYYY-MM-DD')) : [];
        const getUniqueDate = [...new Set(getEventDate)];

        for (const date of getUniqueDate) {
            const filterData = data && data.length !== 0 ?
                data.filter(e => moment.utc(e.exit).tz(timezoneIdentifier).format('YYYY-MM-DD') === date) : [];
            result[date] = filterData;
        }

        for (const [index, item] of getUniqueDate.entries()) {
            let eventData = result[item];
            const chartInfo = generateChartData(eventData, chatXAxis, chartYAxis);

            newChartseries.push({
                name: moment.utc(item).format('DD MMM YYYY'),
                data: chartInfo,
                marker: {
                    name: 'Image symbol',
                    symbol: 'url(https://farm-api.prod02.acuitusag.network/images/vehicle/sm/3_12.png)',
                    width: 35,
                    height: 35,
                },
                visible: index === 0,
            });
        }

        let chartype = {
            type: 'scatter',
            zoomType: 'xy',
        };
        let chartitle = {
            text: ' ',
        };
        // let chartsubtitle = {
        //     text: 'RoundTrip Count',
        // };
        let chartxaxis = {
            categories: chatXAxis,
            title: {
                text: 'Time',
            },
            labels: { step: 2 },
            startOnTick: true,
            endOnTick: true,
            showLastLabel: true,
        };
        let chartyaxis = {
            categories: chartYAxis,
            title: {
                text: 'Region',
            },
            labels: {
                formatter: function () {
                    return this.value;
                },
            },
        };
        let chartlegend = {
            layout: 'horizontal',
            align: 'left',
            verticalAlign: 'top',
            x: 0,
            y: 10,
            floating: true,
            backgroundColor:
                (Highcharts.theme && Highcharts.theme.legendBackgroundColor) ||
                '#FFFFFF',
            borderWidth: 0,
        };
        let chartplotoptions = {
            scatter: {
                marker: {
                    radius: 5,
                    states: {
                        hover: {
                            enabled: true,
                            lineColor: 'rgb(100,100,100)',
                        },
                    },
                },
                states: {
                    hover: {
                        marker: {
                            enabled: false,
                        },
                    },
                },
                events: {
                    legendItemClick: function () {
                        var seriesIndex = this.index;
                        var series = this.chart.series;
                        if (this.visible && this.chart.restIsHidden) {
                            for (var i = 0; i < series.length; i++) {
                                if (series[i].index !== seriesIndex) {
                                    series[i].show();
                                }
                            }
                            this.chart.restIsHidden = false;
                        } else {
                            for (var j = 0; j < series.length; j++) {
                                if (series[j].index !== seriesIndex) {
                                    series[j].hide();
                                }
                            }
                            this.show();
                            this.chart.restIsHidden = true;
                        }
                        return false;
                    },
                },
                showInLegend: true,
            }
        };
        let tooltip = {
            enabled: true,
            useHTML: true,
            formatter: function (e) {
                const regionName = chartYAxis[this.y];
                const date = this.series.name;
                const eventDate = moment(date).format("YYYY-MM-DD");

                const regData = data && data.length !== 0 ? data.filter((e) => e.regionName === regionName && e.exitMapped === eventDate) : [];
                const ts = regData.map((e) => {
                    e["tsMapped"] = moment(e.exit).tz(timezoneIdentifier).format("hh:mm A");
                    return e;
                });
                const time = chatXAxis[this.point.x];
                const endofTime = endOfTime(time);

                const event = ts.filter(item => {
                    const x = `01 01 3000 ${item.tsMapped}`;
                    const y = `01 01 3000 ${endofTime}`;
                    if (moment(y).diff(moment(x), 'minutes') >= 0 && moment(y).diff(moment(x), 'minutes') <= 60) return item;
                    return null;
                });

                const entry = moment(event[0].enter).tz(timezoneIdentifier).format('DD MMM YYYY hh:mm:ss A');
                const exit = moment(event[0].exit).tz(timezoneIdentifier).format('DD MMM YYYY hh:mm:ss A');
                const diff = moment(exit).diff(moment(entry), 'seconds');
                const displayFormat = toDisplayTimeString(secondsToTime(diff, true));

                return `<span>
                <b>${moment(entry).format('hh:mm:ss A')}</b>
                &rarr; <b>${moment(exit).format('hh:mm:ss A')}</b></span><br/>
                &#x23F1; <span><b>${displayFormat}</b></span>`;
            }
        };

        setChartInfo({
            chart: chartype,
            title: chartitle,
            // subtitle: chartsubtitle,
            xAxis: chartxaxis,
            yAxis: chartyaxis,
            legend: chartlegend,
            plotOptions: chartplotoptions,
            series: newChartseries,
            tooltip: tooltip,
            credits: {
                enabled: false,
            },
        });
    }, [data]);

    const endOfTime = (time) => {
        const hour = time.split(':').length !== 0 ? time.split(':')[0] : null;
        const format = time.split(' ').length !== 0 ? time.split(' ')[1] : null;
        const endOfTime = `${hour}:59 ${format}`;
        return endOfTime;
    };

    const checkBetweenTime = (time, fromTime, toTime) => {
        let format = 'hh:mm:ss A';
        let checkTime = moment(time, format);
        let beforeTime = moment(fromTime, format);
        let afterTime = moment(toTime, format);
        return checkTime.isBetween(beforeTime, afterTime);
    };

    const generateChartData = (data, xAxis, yAxis) => {
        const chatXAxis = xAxis;
        const chartYAxis = yAxis
        const chartData = [];

        for (var i = 0; i < chatXAxis.length; i++) {
            const from = chatXAxis[i];
            const to = chatXAxis[i + 1] ? chatXAxis[i + 1] : chatXAxis[0];
            const filter = [];
            for (const item of data) {
                const exit = moment(item.exit).tz(timezoneIdentifier).format('hh:mm:ss A');
                if (checkBetweenTime(exit, from, to)) {
                    filter.push(item);
                }
            }
            const customDateIndex = chatXAxis.indexOf(from);
            if (customDateIndex !== -1) {
                if (filter.length === 0) {
                    chartData.push([customDateIndex, null]);
                } else {
                    for (const reg of filter) {
                        let region = reg.regionName;
                        const regionIndex = chartYAxis.indexOf(region);
                        if (regionIndex !== -1) {
                            chartData.push([customDateIndex, regionIndex]);
                        }
                    }
                }
            }
        }
        return chartData;
    };

    return (
        <div>
            <HighchartsReact highcharts={Highcharts} options={chartInfo} />
        </div>
    );
}